import React, { useState } from "react";
import {
  VStack,
  HStack,
  Text,
  Box,
  Button,
  Badge,
  Input,
  Flex,
  Icon,
  useDisclosure,
  Spinner,
  Textarea,
} from "@chakra-ui/react";

import useThemeColors from "../../../hooks/useThemeColors";
import useIcons from "../../../hooks/useIcons";
import Cad from "../../CustomUi/Cad";
import SectionHeader from "../../CustomUi/SectionHeader";
import {
  GetStudentPromotionDto,
  ApprovePromotionDto,
  RejectPromotionDto,
} from "../../../services/Dtos";
import { useUserContext } from "../../../Contexts/userContext";
import SelectInput from "../../CustomUi/SelectInput";
import CustomPopover from "../../CustomUi/CustomPopover";
import CustomModal from "../../CustomUi/CustomModal";
import CustomAlert from "../../CustomUi/CustomAlert";
import CustomButton from "../../CustomUi/CustomButton";
import Pagination from "../../CustomUi/Pagination";
import { usePromotionService } from "../../../hooks/Domain/usePromotionService";
import {
  promotionStatusFilters,
  promotionTypeOptions,
} from "../../../services/constants";
import { PromotionStatus } from "../../../services/Enums";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import { getClassOptions } from "../../../services/Utils";
import InlineSeparator from "../../CustomUi/InlineSeparator";

const PromotionHistory: React.FC = () => {
  const { user } = useUserContext();
  const { classes } = useSchoolsContext();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const colors = useThemeColors();
  const icons = useIcons();
  const {
    getSessionPromotions,
    approvePromotion,
    rejectPromotion,
    currentSession,
    isLoading,
    selectedPromotion,
    setSelectedPromotion,
    statusFilter,
    setStatusFilter,
    rejectionReason,
    setRejectionReason,
    searchTerm,
    setSearchTerm,
    fromClassFilter,
    setFromClassFilter,
    toClassFilter,
    setToClassFilter,
    promotionTypeFilter,
    setPromotionTypeFilter,
    filteredPromotions,
    getTypeInfo,
    getStatusInfo,
    transformedPromotions,
    hasAttemptedFetch,
    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    totalRecords,
  } = usePromotionService();

  const {
    open: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();
  const {
    open: isRejectModalOpen,
    onOpen: onRejectModalOpen,
    onClose: onRejectModalClose,
  } = useDisclosure();

  // Load promotions
  const loadPromotions = async (page = currentPage) => {
    await getSessionPromotions(currentSession?.id || "", {
      status: statusFilter !== "all" ? statusFilter : undefined,
      fromClassId: fromClassFilter || undefined,
      toClassId: toClassFilter || undefined,
      type: promotionTypeFilter !== "all" ? promotionTypeFilter : undefined,
      searchTerm: searchTerm || undefined,
      pageNumber: page,
      pageSize: pageSize,
    });
  };

  // Handle approval
  const handleApprove = async (promotionId: string) => {
    const request: ApprovePromotionDto = {
      promotionId,
      approvedById: user?.id || "",
    };
    await approvePromotion(request, () => {
      // Refetch promotions after successful approval
      loadPromotions(currentPage);
    });
  };

  // Handle rejection
  const handleReject = async () => {
    if (!selectedPromotion || !rejectionReason.trim()) return;

    const request: RejectPromotionDto = {
      promotionId: selectedPromotion.id,
      rejectionReason: rejectionReason.trim(),
      rejectedById: user?.id || "",
    };

    await rejectPromotion(request, () => {
      // Refetch promotions after successful rejection
      loadPromotions(currentPage);
    });

    setRejectionReason("");
    onRejectModalClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const openDetailModal = (promotion: GetStudentPromotionDto) => {
    setSelectedPromotion(promotion);
    onDetailModalOpen();
  };

  const openRejectModal = (promotion: GetStudentPromotionDto) => {
    setSelectedPromotion(promotion);
    setRejectionReason("");
    onRejectModalOpen();
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    setCurrentPage(newPage);
    loadPromotions(newPage);
  };

  const handleNextPage = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    setCurrentPage(newPage);
    loadPromotions(newPage);
  };

  // Filter options
  const classOptions = getClassOptions(classes, true);

  return (
    <VStack gap={{ base: 2, md: 4 }} align="stretch">
      {/* Controls */}
      <Cad h={"auto"} position={"sticky"} top={-4} zIndex={2}>
        <VStack gap={4} align="stretch">
          <SectionHeader
            title="Promotion History"
            subtitle={`Review and manage promotion requests. Current Session: ${
              currentSession?.sessionName || "Not Set"
            }`}
            icon={icons.main.promotion}
          />

          {/* Search and Filters */}
          <HStack
            gap={3}
            flexWrap={{ base: "wrap", md: "nowrap" }}
            alignItems="flex-start"
          >
            {filteredPromotions.length > 0 && (
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                flex={{ base: "1", md: "1" }}
                minW={{ base: "200px", md: "250px" }}
              />
            )}

            <CustomPopover
              setOpen={setIsFilterOpen}
              open={isFilterOpen}
              w={"300px"}
              header={<Text fontWeight="semibold">Filters</Text>}
              trigger={
                <Button
                  size="sm"
                  variant="surface"
                  colorPalette={colors.schoolAccent.primary}
                >
                  <Icon as={icons.utility.filter} />
                  Fetch History
                </Button>
              }
            >
              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Promotion Status
                  </Text>
                  <SelectInput
                    placeholder={"Promotion Status"}
                    name={""}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    collection={promotionStatusFilters}
                    size="sm"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    From Class
                  </Text>
                  <SelectInput
                    placeholder={"All Classes"}
                    name={""}
                    value={fromClassFilter}
                    onChange={(e) => setFromClassFilter(e.target.value)}
                    collection={classOptions}
                    size="sm"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    To Class
                  </Text>
                  <SelectInput
                    placeholder={"All Classes"}
                    name={""}
                    value={toClassFilter}
                    onChange={(e) => setToClassFilter(e.target.value)}
                    collection={classOptions}
                    size="sm"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Promotion Type
                  </Text>
                  <SelectInput
                    placeholder={"All Types"}
                    name={""}
                    value={promotionTypeFilter}
                    onChange={(e) =>
                      setPromotionTypeFilter(e.target.value as any)
                    }
                    collection={promotionTypeOptions}
                    size="sm"
                  />
                </Box>

                <CustomButton
                  size="sm"
                  icon={icons.utility.refresh}
                  label="Fetch"
                  onClick={() => {
                    loadPromotions(1);
                    setIsFilterOpen(false);
                  }}
                  loading={isLoading}
                  disabled={!currentSession}
                  tooltipContent={
                    !currentSession
                      ? "No active session found. Please set a current session for the school."
                      : ""
                  }
                />
              </VStack>
            </CustomPopover>
          </HStack>

          {/* Summary Stats */}
          {transformedPromotions.length > 0 && (
            <HStack gap={4} wrap="wrap">
              <Badge colorPalette="blue" variant="subtle">
                Total: {transformedPromotions.length}
              </Badge>
              <Badge colorPalette="yellow" variant="subtle">
                Pending:{" "}
                {
                  transformedPromotions.filter(
                    (p) => p.status === PromotionStatus.Pending
                  ).length
                }
              </Badge>
              <Badge colorPalette="green" variant="subtle">
                Promoted:{" "}
                {
                  transformedPromotions.filter(
                    (p) => p.status === PromotionStatus.Promoted
                  ).length
                }
              </Badge>
              <Badge colorPalette="red" variant="subtle">
                Rejected:{" "}
                {
                  transformedPromotions.filter(
                    (p) => p.status === PromotionStatus.Rejected
                  ).length
                }
              </Badge>
            </HStack>
          )}
        </VStack>
      </Cad>

      {/* Promotions List */}
      <Cad flex={1}>
        {isLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="lg" color={colors.schoolAccent.primary} />
          </Flex>
        ) : filteredPromotions.length === 0 ? (
          <Box textAlign="center" py={8} color={colors.text.secondary}>
            <Icon as={icons.main.promotion} boxSize={8} mb={2} opacity={0.5} />
            <Text fontSize="sm">
              {!hasAttemptedFetch
                ? "Use the filters above and click 'Fetch History' to fetch promotion history."
                : (() => {
                    const hasFilters =
                      statusFilter !== "all" ||
                      fromClassFilter ||
                      toClassFilter ||
                      promotionTypeFilter !== "all" ||
                      searchTerm;

                    return hasFilters
                      ? "No promotion records match the current filter criteria. Try adjusting your filters."
                      : "No promotion records found for the current session.";
                  })()}
            </Text>
            {currentSession && transformedPromotions.length === 0 && (
              <Text fontSize="xs" mt={1}>
                Current Session: {currentSession.sessionName}
              </Text>
            )}
            {transformedPromotions.length > 0 && (
              <Text fontSize="xs" mt={1}>
                Total Records: {transformedPromotions.length} • Filtered:{" "}
                {filteredPromotions.length}
              </Text>
            )}
          </Box>
        ) : (
          <Box>
            <VStack align="stretch" gap={2}>
              {filteredPromotions.map((promotion) => {
                const statusInfo = getStatusInfo(promotion.status);
                const typeInfo = getTypeInfo(promotion.type);

                return (
                  <Box
                    key={promotion.id}
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={colors.border.subtle}
                    bg={colors.background.secondary}
                    transition="all 0.2s"
                    _hover={{
                      bg: statusInfo.color + "/5",
                    }}
                  >
                    <Flex justify="space-between" align="start" mb={3}>
                      <VStack align="start" gap={1}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {promotion.studentName}
                        </Text>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          {promotion.fromClassName} → {promotion.toClassName}
                        </Text>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          {promotion.sessionName} • {promotion.termName}
                        </Text>
                      </VStack>

                      <VStack align="end" gap={2}>
                        <HStack gap={2}>
                          <Badge
                            colorPalette={statusInfo.color as any}
                            variant="subtle"
                            fontSize="xs"
                          >
                            <HStack gap={1}>
                              <Icon as={statusInfo.icon} />
                              <Text>{statusInfo.label}</Text>
                            </HStack>
                          </Badge>
                          <Badge
                            colorPalette={typeInfo.color as any}
                            variant="subtle"
                            fontSize="xs"
                          >
                            {typeInfo.label}
                          </Badge>
                        </HStack>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          {formatDate(promotion.createdAt)}
                        </Text>
                      </VStack>
                    </Flex>

                    {/* Performance Metrics */}
                    <HStack gap={4} mb={3} wrap="wrap">
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Average Score
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {promotion.studentAverageScore.toFixed(1)}%
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Position
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {promotion.studentPositionInClass}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Attendance
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {promotion.studentAttendancePercentage.toFixed(1)}%
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color={colors.text.secondary}>
                          Subjects Passed
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {promotion.allSubjectsPassed ? "All" : "Some Failed"}
                        </Text>
                      </Box>
                    </HStack>

                    {/* Actions */}
                    <HStack gap={2} justify="flex-end">
                      <CustomButton
                        size="xs"
                        variant="outline"
                        icon={icons.utility.view}
                        label="View Details"
                        onClick={() => openDetailModal(promotion)}
                      />

                      {promotion.status === PromotionStatus.Pending && (
                        <>
                          <CustomButton
                            size="xs"
                            colorPalette="green"
                            icon={icons.utility.check}
                            label="Approve"
                            onClick={() => handleApprove(promotion.id)}
                            loading={isLoading}
                          />
                          <CustomButton
                            size="xs"
                            colorPalette="red"
                            icon={icons.utility.cancel}
                            label="Reject"
                            onClick={() => openRejectModal(promotion)}
                          />
                        </>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            currentPageRecords={filteredPromotions.length}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            loading={isLoading}
          />
        )}
      </Cad>

      {/* Detail Modal */}
      <CustomModal
        hasChanges={true}
        isOpen={isDetailModalOpen}
        onClose={onDetailModalClose}
        handleClose={onDetailModalClose}
        title="Promotion Details"
        icon={icons.main.promotion}
        mode="view"
        actionText="Close"
        handleAction={onDetailModalClose}
        hideCancelButton={true}
      >
        {selectedPromotion && (
          <VStack gap={6} align="stretch">
            {/* Student Information Section */}
            <Box>
              <InlineSeparator label="Student Information" />

              <VStack gap={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Name:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.studentName}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    From Class:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.fromClassName}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    To Class:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.toClassName}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Academic Information Section */}
            <Box>
              <InlineSeparator label="Academic Information" />
              <VStack gap={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Session:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.sessionName}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Term:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.termName}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Average Score:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.studentAverageScore.toFixed(1)}%
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Position:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.studentPositionInClass}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Attendance:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {selectedPromotion.studentAttendancePercentage.toFixed(1)}%
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    All Subjects Passed:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    <Badge
                      colorPalette={
                        selectedPromotion.allSubjectsPassed ? "green" : "red"
                      }
                      variant="subtle"
                      fontSize="xs"
                    >
                      {selectedPromotion.allSubjectsPassed ? "Yes" : "No"}
                    </Badge>
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Promotion Details Section */}
            <Box>
              <InlineSeparator label="Promotion Details" />
              <VStack gap={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Status:
                  </Text>
                  <Badge
                    colorPalette={getStatusInfo(selectedPromotion.status).color}
                    variant="subtle"
                    fontSize="xs"
                  >
                    <HStack gap={1}>
                      <Icon as={getStatusInfo(selectedPromotion.status).icon} />
                      <Text>
                        {getStatusInfo(selectedPromotion.status).label}
                      </Text>
                    </HStack>
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Type:
                  </Text>
                  <Badge
                    colorPalette={getTypeInfo(selectedPromotion.type).color}
                    variant="subtle"
                    fontSize="xs"
                  >
                    {getTypeInfo(selectedPromotion.type).label}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color={colors.text.secondary}>
                    Created:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatDate(selectedPromotion.createdAt)}
                  </Text>
                </HStack>
                {selectedPromotion.promotedAt && (
                  <HStack justify="space-between">
                    <Text fontSize="sm" color={colors.text.secondary}>
                      Promoted:
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatDate(selectedPromotion.promotedAt)}
                    </Text>
                  </HStack>
                )}
                {selectedPromotion.rejectionReason && (
                  <Box>
                    <Text fontSize="sm" color={colors.text.secondary} mb={2}>
                      Rejection Reason:
                    </Text>
                    <Box
                      p={3}
                      bg={colors.background.secondary}
                      borderRadius="md"
                      borderLeft="4px"
                      borderColor="red.400"
                    >
                      <Text fontSize="sm">
                        {selectedPromotion.rejectionReason}
                      </Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            </Box>
          </VStack>
        )}
      </CustomModal>

      {/* Reject Modal */}
      <CustomModal
        isOpen={isRejectModalOpen}
        onClose={onRejectModalClose}
        handleClose={onRejectModalClose}
        handleAction={handleReject}
        isLoading={isLoading}
        hasChanges={rejectionReason.trim().length > 0}
        title="Reject Promotion"
        icon={icons.utility.cancel}
        actionText="Reject Promotion"
      >
        <VStack gap={4} align="stretch">
          <CustomAlert
            status="warning"
            title="Rejection Action"
            message={`You are about to reject the promotion for ${selectedPromotion?.studentName}. Please provide a reason.`}
          />

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Rejection Reason
            </Text>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter the reason for rejection..."
              rows={4}
            />
          </Box>
        </VStack>
      </CustomModal>
    </VStack>
  );
};

export default PromotionHistory;
